/**
 * Mobile-specific optimizations and utilities
 */

// Detect if user is on mobile device
export const isMobileDevice = (): boolean => {
  if (typeof window === "undefined") return false;
  
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  ) || window.innerWidth < 768;
};

// Detect if user is on iOS
export const isIOS = (): boolean => {
  if (typeof window === "undefined") return false;
  
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

// Optimize touch target size for mobile
export const getMinTouchSize = (): number => {
  return 48; // 48px minimum recommended by WCAG
};

// Add passive event listeners for better scroll performance
export const addPassiveEventListener = (
  element: HTMLElement | Window,
  event: string,
  handler: EventListener
) => {
  element.addEventListener(event, handler, { passive: true });
};

// Prevent zoom on double tap (iOS)
export const preventDoubleTapZoom = (element: HTMLElement) => {
  let lastTap = 0;
  
  element.addEventListener("touchend", (event) => {
    const currentTime = new Date().getTime();
    const tapLength = currentTime - lastTap;
    
    if (tapLength < 500 && tapLength > 0) {
      event.preventDefault();
    }
    
    lastTap = currentTime;
  });
};

// Optimize keyboard for mobile inputs
export const setMobileKeyboard = (
  input: HTMLInputElement,
  type: "email" | "tel" | "url" | "number" | "search"
) => {
  if (!isMobileDevice()) return;
  
  input.setAttribute("inputmode", type);
  
  // Add mobile-specific attributes
  if (type === "email") {
    input.setAttribute("autocomplete", "email");
    input.setAttribute("autocapitalize", "off");
    input.setAttribute("autocorrect", "off");
  } else if (type === "tel") {
    input.setAttribute("autocomplete", "tel");
  }
};

// Debounce for better mobile performance
export const debounce = <T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Throttle for scroll/resize events on mobile
export const throttle = <T extends (...args: unknown[]) => unknown>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

// Get optimal image size for mobile
export const getOptimalImageSize = (): { width: number; height: number } => {
  if (typeof window === "undefined") {
    return { width: 1200, height: 800 };
  }
  
  const dpr = window.devicePixelRatio || 1;
  const screenWidth = window.innerWidth;
  
  return {
    width: Math.min(screenWidth * dpr, 1920),
    height: Math.min((screenWidth * dpr * 2) / 3, 1280),
  };
};

// Check if device supports touch
export const isTouchDevice = (): boolean => {
  if (typeof window === "undefined") return false;
  
  return (
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    ('msMaxTouchPoints' in navigator && (navigator as Navigator & { msMaxTouchPoints: number }).msMaxTouchPoints > 0)
  );
};

// Optimize modal/dialog for mobile
export const optimizeModalForMobile = (modalElement: HTMLElement) => {
  if (!isMobileDevice()) return;
  
  // Prevent background scrolling when modal is open
  document.body.style.overflow = "hidden";
  document.body.style.position = "fixed";
  document.body.style.width = "100%";
  
  // Make modal full screen on mobile
  modalElement.style.maxHeight = "100vh";
  modalElement.style.borderRadius = "0";
  
  return () => {
    // Cleanup function
    document.body.style.overflow = "";
    document.body.style.position = "";
    document.body.style.width = "";
  };
};

// Add pull-to-refresh hint
export const enablePullToRefresh = (
  element: HTMLElement,
  onRefresh: () => Promise<void>
) => {
  if (!isMobileDevice()) return;
  
  let startY = 0;
  let currentY = 0;
  let pulling = false;
  
  element.addEventListener("touchstart", (e) => {
    if (element.scrollTop === 0) {
      startY = e.touches[0].clientY;
      pulling = true;
    }
  }, { passive: true });
  
  element.addEventListener("touchmove", (e) => {
    if (!pulling) return;
    
    currentY = e.touches[0].clientY;
    const diff = currentY - startY;
    
    if (diff > 80) {
      // Show refresh indicator
      element.style.transform = `translateY(${Math.min(diff, 120)}px)`;
    }
  }, { passive: true });
  
  element.addEventListener("touchend", async () => {
    if (!pulling) return;
    
    const diff = currentY - startY;
    
    if (diff > 80) {
      await onRefresh();
    }
    
    element.style.transform = "";
    pulling = false;
  });
};
